// Registries for the 2x scene painters. Building kinds ("fronts"), roof materials and roof props register themselves
// from hd-fronts-<region>.ts files (listed in hd-fronts-index.ts), so a region never edits another region's painters.
import type { HdBuilding } from '../../common/src/data';
import type { G } from './hd-kit';

/** record a light in scene hd px: x, y, w, h, color, alpha, wet reflection, live (flickering) sign */
export type LightFn = (x: number, y: number, w: number, h: number, color: string, a: number, refl?: boolean, live?: boolean) => void;

/** What a front painter gets. Coordinates are hd px in the scene canvas; (x, y) is the top-left of the south wall. */
export interface FrontCtx {
  g: G; b: HdBuilding; x: number; y: number; w: number; h: number;
  /** ground floor height and top y of the ground floor */
  gf: number; gy: number;
  /** rows of plain wall above the shopfront */
  upper: number; seed: number; wall: string; sign: HdBuilding['sign']; L: LightFn;
}
export interface FrontSpec {
  /** wall colour (default K.wall) */
  wall?: string;
  /** upper floors: 'windows' (default, when there is room), 'curtain' (glass tower) or 'none' */
  upper?: 'windows' | 'curtain' | 'none';
  /** wall air-conditioners between the upper windows */
  wallAc?: boolean;
  paint: (c: FrontCtx) => void;
}
export interface RoofSpec {
  /** false: the painter draws its own frame (no parapet, e.g. a sloped tile roof). Default true. */
  parapet?: boolean;
  paint: (g: G, x: number, y: number, w: number, h: number, seed: number) => void;
}
export interface RoofPropCtx { g: G; x: number; y: number; bx: number; by: number; p: (string | number)[]; L: LightFn }

export const FRONTS = new Map<string, FrontSpec>();
export const ROOFS = new Map<string, RoofSpec>();
export const ROOF_PROPS = new Map<string, (c: RoofPropCtx) => void>();

function add<T>(map: Map<string, T>, what: string, name: string, v: T) {
  if (map.has(name)) throw new Error(`hd ${what} "${name}" is registered twice`);
  map.set(name, v);
}
export const registerFront = (kind: string, spec: Omit<FrontSpec, 'paint'>, paint: FrontSpec['paint']) => add(FRONTS, 'front', kind, { ...spec, paint });
export const registerRoof = (name: string, spec: RoofSpec) => add(ROOFS, 'roof', name, spec);
export const registerRoofProp = (kind: string, paint: (c: RoofPropCtx) => void) => add(ROOF_PROPS, 'roof prop', kind, paint);
